import { createContext, useContext, useEffect, useRef, useState } from 'react';

import { useSnackbar } from 'notistack';
import { useLocation } from 'react-router-dom';

import { nestedLocationsApi, sortOrderParams } from '../../../axios/nestedLocations';
import { useDebounce, useFeatureFlags, useLanguage } from '../../../hooks';
import {
  useGetAccountStatusWithLocations,
  useGetNestedLocations,
} from '../../../queries/nestedLocations';
import { NestedLocation } from '../../../types/nestedLocations';
import { getNextSortByNameOrder, SortByNameOrder } from '../../../utils/sortByName';
import { buildLocationIndexes, getLocationAncestors } from '../utils/LocationsHelpers';
import {
  MAX_CHILD_LOCATIONS_PER_PAGE,
  MAX_ROOT_LOCATIONS_PER_PAGE,
} from '../utils/LocationsParameters';
import { SelectedStatus, useLocationsReducer } from '../utils/LocationsReducer';

/** One open place at the moment the order changed, with what decides its survival. */
interface ReopenEntry {
  id: number;
  parentId: number | null;
  depth: number;
}

/**
 * A reopen plan, stamped with the query it was captured for.
 *
 * The stamp is what keeps an unrelated root settlement from consuming it. The effect that
 * runs the pass fires on every root update, so without it a plan left armed by a failed
 * revalidation would replay on the next page change or search.
 */
interface ReopenPlan {
  entries: ReopenEntry[];
  order: SortByNameOrder;
  page: number;
}

interface NestedLocationsContextType {
  locationsState: ReturnType<typeof useLocationsReducer>[0];
  locationsDispatch: ReturnType<typeof useLocationsReducer>[1];
  searchQuery: string;
  setSearchQuery: React.Dispatch<React.SetStateAction<string>>;
  debouncedSearchValue: string;
  setDebouncedSearchValue: React.Dispatch<React.SetStateAction<string>>;
  currentPage: number;
  setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
  totalLocations: number;
  isLoadingLocationsTree: boolean;
  numberOfPages: number;
  handleExpandLocation: (locationId: number) => void;
  handleLoadMore: (parentLocation: NestedLocation) => void;
  handleSearch: (query: string) => void;
  hasNestableLocations: boolean;
  hasOldLocations: boolean;
  numberOfNestableLocations: number;
  isLoadingAccountStatusWithLocations: boolean;
  loadingChildren: Record<number, boolean>;
  maxDepth: number;
  maxNestedLocationsPerParent: number;
  maxNumberOfLocations: number;
  sortOrder: SortByNameOrder;
  cycleSortOrder: () => void;
  handleLocationDeleted: (locationId: number) => Promise<void>;
}

export const NestedLocationsContext = createContext<NestedLocationsContextType>({
  locationsState: {
    locationById: new Map<number, NestedLocation>(),
    childrenByParentId: new Map<number, number[]>(),
    selectedLocations: new Map<number, SelectedStatus>(),
    allLocationsSelected: false,
  },
  locationsDispatch: () => undefined,
  searchQuery: '',
  setSearchQuery: () => '',
  debouncedSearchValue: '',
  setDebouncedSearchValue: () => '',
  currentPage: 1,
  setCurrentPage: () => 1,
  totalLocations: 0,
  isLoadingLocationsTree: false,
  numberOfPages: 0,
  handleExpandLocation: () => undefined,
  handleLoadMore: () => undefined,
  handleSearch: () => undefined,
  hasNestableLocations: false,
  hasOldLocations: false,
  numberOfNestableLocations: 0,
  isLoadingAccountStatusWithLocations: true,
  loadingChildren: {},
  maxDepth: 0,
  maxNestedLocationsPerParent: 0,
  maxNumberOfLocations: 0,
  sortOrder: 'unsorted',
  cycleSortOrder: () => undefined,
  handleLocationDeleted: () => Promise.resolve(),
});

export const NestedLocationsProvider = ({ children }: { children: React.ReactNode }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchValue, setDebouncedSearchValue] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [sortOrder, setSortOrder] = useState<SortByNameOrder>('unsorted');
  const pendingReopen = useRef<ReopenPlan | null>(null);
  /**
   * Which order change a branch response belongs to. Browser-only, never a request
   * parameter. The root needs none: its query key already isolates it.
   */
  const branchGeneration = useRef(0);
  /** Which reopen pass owns the tree, so two overlapping passes cannot both write. */
  const reopenPassId = useRef(0);
  const branchAbort = useRef<AbortController | null>(null);
  // Created on the first render, not on the first order change: otherwise the very first
  // change has nothing to abort.
  if (!branchAbort.current) branchAbort.current = new AbortController();
  const [locationsState, locationsDispatch] = useLocationsReducer();
  const { enqueueSnackbar } = useSnackbar();
  const lang = useLanguage();

  // Record of locations that are currently loading children.
  // {locationId: number => isLoading: boolean}
  const [loadingChildren, setLoadingChildren] = useState<Record<number, boolean>>({});

  const debouncedSearch = useDebounce(() => {
    if (searchQuery.length > 1 || searchQuery === '') {
      setDebouncedSearchValue(searchQuery);
    }
  }, 300);

  const { getPremiumFeature } = useFeatureFlags();
  const maxDepth = parseInt(getPremiumFeature('max_depth_nestable_location') || '1');
  const maxNumberOfLocations = parseInt(getPremiumFeature('max_nestable_locations') || '0');
  const maxNestedLocationsPerParent = parseInt(getPremiumFeature('max_nested_locations') || '0');

  const {
    data: rootLocationsResponse,
    isLoading: isLoadingLocationsTree,
    isFetching: isFetchingLocationsTree,
    dataUpdatedAt,
    refetch: refetchRootLocations,
  } = useGetNestedLocations({
    searchValue: debouncedSearchValue,
    currentPage,
    itemsPerPage: MAX_ROOT_LOCATIONS_PER_PAGE,
    order: sortOrder,
  });

  const getChildrenLocationsFromBackend = (
    locationId: number,
    offset = 0,
    limit = MAX_CHILD_LOCATIONS_PER_PAGE,
  ) =>
    nestedLocationsApi.getNestedLocations(
      {
        parent_id: locationId,
        only_children: true,
        limit,
        offset,
        ...sortOrderParams(sortOrder),
      },
      branchAbort.current?.signal,
    );

  /** True once a newer order change has taken ownership of the branch requests. */
  const isSupersededBranchResponse = (generation: number) =>
    branchGeneration.current !== generation;

  /**
   * The single writer of the loading flag, and the single place the ownership check has
   * to hold. That flag doubles as the re-entrancy guard on the branch handlers, so a
   * superseded completion clearing it would let a duplicate fetch through.
   */
  const settleBranchLoad = (locationId: number, generation: number) => {
    if (isSupersededBranchResponse(generation)) return;
    setLoadingChildren((previous) => ({ ...previous, [locationId]: false }));
  };

  /**
   * The one place a branch's children are fetched and written.
   *
   * All four callers had the same fetch, guarded dispatch, guarded toast and settle
   * written out separately, and they had already drifted: the reopen pass swallowed
   * genuine failures while the other three reported them.
   *
   * Returns the response so a caller can read what came back, or null when nothing was
   * written because the request failed or its episode is over.
   */
  /**
   * `limit` is for the one caller that replaces a span instead of extending it. At offset 0
   * the reducer replaces the parent's children, so a refetch there has to ask for as many
   * rows as were loaded or the branch silently shrinks to one batch.
   */
  const loadChildrenBatch = async (
    targetLocationId: number,
    offset: number,
    generation: number,
    limit?: number,
  ) => {
    setLoadingChildren((previous) => ({ ...previous, [targetLocationId]: true }));

    try {
      const response = await getChildrenLocationsFromBackend(targetLocationId, offset, limit);
      if (isSupersededBranchResponse(generation)) return null;

      locationsDispatch({
        type: 'update_location_children',
        payload: {
          targetLocationId,
          newLocations: response.locations,
          has_more_children: response.next_page_exists,
          offset,
        },
      });
      return response;
    } catch {
      // An abort is not a failure the user asked about: it only happens because a newer
      // order took over, and that is exactly what the ownership check reads.
      if (!isSupersededBranchResponse(generation)) {
        enqueueSnackbar(lang.locations.table.loadMoreError, { variant: 'error' });
      }
      return null;
    } finally {
      settleBranchLoad(targetLocationId, generation);
    }
  };

  /**
   * What was open when the order changed, and deep enough to decide survival.
   *
   * Captured BEFORE the refetch on purpose: `reset_location_state` rebuilds
   * `locationById` from the root response alone, and the depth of a loaded child is only
   * derivable from the index that is about to be replaced.
   */
  const captureOpenBranches = (order: SortByNameOrder, page: number): ReopenPlan => ({
    order,
    page,
    entries: Array.from(locationsState.locationById.values())
      .filter((location) => location.expanded)
      .map((location) => ({
        id: location.id,
        parentId: location.parent_id,
        depth: getLocationAncestors(location.id, locationsState.locationById).length,
      }))
      .sort((a, b) => a.depth - b.depth),
  });

  /**
   * Reopens what survives the new order, one level at a time.
   *
   * A place reopens only if its parent reopened and it comes back in the first batch of
   * that parent's children under the new order. Whatever does not appear stays collapsed,
   * with nothing reported: the tree is not claiming a position the server did not give it.
   *
   * Levels are serial because survival at level N is decided by what level N-1 returned.
   */
  const reopenSurvivingBranches = async (plan: ReopenPlan, rootIds: Set<number>) => {
    const generation = branchGeneration.current;
    // A cached serve and its revalidation both settle under one generation, so the
    // generation alone cannot tell two overlapping passes apart. Without this, the older
    // pass keeps writing next to the newer one.
    reopenPassId.current += 1;
    const passId = reopenPassId.current;
    const isSuperseded = () =>
      isSupersededBranchResponse(generation) || reopenPassId.current !== passId;

    const childrenOf = new Map<number, ReopenEntry[]>();
    plan.entries.forEach((entry) => {
      if (entry.parentId === null) return;
      childrenOf.set(entry.parentId, [...(childrenOf.get(entry.parentId) ?? []), entry]);
    });

    let level = plan.entries.filter(
      (entry) => entry.parentId === null && rootIds.has(entry.id),
    );

    while (level.length > 0) {
      if (isSuperseded()) return;

      const results = await Promise.all(
        level.map(async (entry) => {
          // Explicit true, never a toggle: see the payload doc on set_location_expanded.
          locationsDispatch({
            type: 'set_location_expanded',
            payload: { locationId: entry.id, expanded: true },
          });

          const response = await loadChildrenBatch(entry.id, 0, generation);
          if (!response || isSuperseded()) return [];

          const arrived = new Set(response.locations.map((location) => location.id));
          return (childrenOf.get(entry.id) ?? []).filter((child) => arrived.has(child.id));
        }),
      );

      level = results.flat();
    }
  };

  /**
   * An order change is a reset of the tree's loaded state, not a view toggle. The root
   * returns to page 1, because a page number is a position inside a sequence the change
   * destroys, and the loaded children go with it: the next batch's offset derives from
   * how many are loaded, so emptying the list returns it to a batch boundary.
   *
   * Guarded on the order actually changing, or re-entering the tree would discard the
   * page and the open branches of a user who never sorted.
   */
  const handleSortOrderChange = (order: SortByNameOrder) => {
    if (order === sortOrder) return;

    // Take ownership before anything else. Everything in flight belongs to the previous
    // order from this line on, and the abort keeps those requests from arriving at all
    // rather than merely being ignored when they do.
    branchGeneration.current += 1;
    branchAbort.current?.abort();
    branchAbort.current = new AbortController();

    pendingReopen.current = captureOpenBranches(order, 1);
    setLoadingChildren({});
    setCurrentPage(1);
    setSortOrder(order);
  };

  const cycleSortOrder = () => handleSortOrderChange(getNextSortByNameOrder(sortOrder));

  const handleExpandLocation = async (locationId: number) => {
    locationsDispatch({ type: 'set_location_expanded', payload: { locationId } });
    if (loadingChildren[locationId]) return;
    if (locationsState.childrenByParentId.get(locationId)?.length > 0) return;

    await loadChildrenBatch(locationId, 0, branchGeneration.current);
  };

  const handleLoadMore = async (parentLocation: NestedLocation) => {
    if (loadingChildren[parentLocation.id]) return;
    const currentChildren = locationsState.childrenByParentId.get(parentLocation.id) || [];

    await loadChildrenBatch(parentLocation.id, currentChildren.length, branchGeneration.current);
  };

  /**
   * Removes a deleted place and, with the order active, refetches the level it was on.
   *
   * With the order living on the server the client cannot know where the remaining rows
   * belong: closing the gap locally would have the tree claim a position the server never
   * gave it. Unsorted it keeps removing the row locally, which is what it does today.
   *
   * The level comes from the row being deleted, which is what makes the refetch ask for
   * the branch actually on screen rather than a fixed endpoint.
   *
   * Deleting a ROOT goes through the root query, and that rebuilds the whole index from
   * an only_root response, so every open branch in the tree would collapse. The plan is
   * seeded first so the reopen pass restores what survives, the same way an order change
   * does. The deleted root can sit in that capture harmlessly: the pass filters level one
   * through the refetched page, which no longer contains it.
   *
   * Deletion is the only mutation that happens without leaving the tree. Create, edit and
   * view navigate to their own routes, and the way back from those is covered by the
   * route-scoped reset.
   */
  const handleLocationDeleted = async (locationId: number) => {
    const parentId = locationsState.locationById.get(locationId)?.parent_id ?? null;
    // The span to refetch is the one on screen BEFORE the delete. Position relative to the
    // dispatch below is not what secures that: `childrenByParentId` is copied by the reducer
    // and its arrays rebuilt with `filter`, so this render's closure keeps the pre-delete
    // length either way. Narrow on purpose. The reducer is not copy-only in general: it
    // assigns `parent.children_count` on an object shared with the previous state.
    const loadedInBranch = parentId === null
      ? 0
      : (locationsState.childrenByParentId.get(parentId)?.length ?? 0);

    if (sortOrder !== 'unsorted' && parentId === null) {
      pendingReopen.current = captureOpenBranches(sortOrder, currentPage);
    }

    locationsDispatch({ type: 'delete_location', payload: { locationId } });

    if (sortOrder === 'unsorted') return;

    if (parentId === null) {
      // `refetch` resolves rather than throws, so an await alone would swallow the error
      // and leave the tree showing a row the server no longer has.
      const result = await refetchRootLocations();
      if (result.isError) {
        pendingReopen.current = null;
        enqueueSnackbar(lang.locations.table.loadMoreError, { variant: 'error' });
      }
      return;
    }

    await loadChildrenBatch(parentId, 0, branchGeneration.current, loadedInBranch || undefined);
  };

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    debouncedSearch();
    setCurrentPage(1);
  };

  const { data: accountStatusWithLocations, isPending: isLoadingAccountStatusWithLocations } =
    useGetAccountStatusWithLocations();

  const hasNestableLocations = accountStatusWithLocations?.has_nestable_locations ?? false;
  const hasOldLocations = accountStatusWithLocations?.has_old_locations ?? false;
  const numberOfNestableLocations = accountStatusWithLocations?.number_of_nestable_locations ?? 0;

  useEffect(() => {
    if (dataUpdatedAt > 0) {
      const locationIndexes = buildLocationIndexes(rootLocationsResponse.locations);
      locationsDispatch({
        type: 'reset_location_state',
        payload: locationIndexes,
      });
      setLoadingChildren({});

      const plan = pendingReopen.current;
      if (!plan) return;

      // See ReopenPlan: stamped to its query so an unrelated settlement cannot consume it.
      if (plan.order !== sortOrder || plan.page !== currentPage) {
        pendingReopen.current = null;
        return;
      }

      // With a search active the response already carries every level and the table
      // force-expands what matched, so there is nothing to reopen. Running the pass here
      // would fetch each open branch's children with no `search` and replace the filtered
      // rows with an unfiltered first ten.
      if (debouncedSearchValue !== '') {
        pendingReopen.current = null;
        return;
      }

      // Held while the query is still fetching. A cached entry settles first and the
      // revalidation resets the tree again, so the pass has to run on that settlement too
      // or everything it reopened disappears. Suppressing the second reset instead would
      // also suppress the one a delete's invalidation depends on.
      if (!isFetchingLocationsTree) pendingReopen.current = null;
      void reopenSurvivingBranches(plan, new Set(locationIndexes.locationById.keys()));
    }
  }, [dataUpdatedAt]);

  // Cancels whatever branch request is in flight when the provider really goes away,
  // which is leaving the Locations feature rather than moving between its five routes.
  useEffect(() => () => branchAbort.current?.abort(), []);

  const { pathname } = useLocation();
  const previousPathname = useRef(pathname);

  // Re-entering the tree from any Locations route leaves the control unsorted.
  //
  // It watches the pathname rather than a mount because the five Locations routes render
  // the provider as siblings at the same depth with no key (App.tsx:787-835), so React
  // reconciles by type and this instance survives every navigation between them.
  useEffect(() => {
    if (previousPathname.current === pathname) return;
    previousPathname.current = pathname;
    handleSortOrderChange('unsorted');
  }, [pathname]);

  const numberOfPages = Math.ceil(
    rootLocationsResponse.total_locations / MAX_ROOT_LOCATIONS_PER_PAGE,
  );

  return (
    <NestedLocationsContext.Provider
      value={{
        locationsState,
        locationsDispatch,
        searchQuery,
        setSearchQuery,
        debouncedSearchValue,
        setDebouncedSearchValue,
        currentPage,
        setCurrentPage,
        totalLocations: rootLocationsResponse.total_locations,
        isLoadingLocationsTree,
        numberOfPages,
        handleExpandLocation,
        handleLoadMore,
        handleSearch,
        hasNestableLocations,
        hasOldLocations,
        numberOfNestableLocations,
        isLoadingAccountStatusWithLocations,
        loadingChildren,
        maxDepth,
        maxNestedLocationsPerParent,
        maxNumberOfLocations,
        sortOrder,
        cycleSortOrder,
        handleLocationDeleted,
      }}
    >
      {children}
    </NestedLocationsContext.Provider>
  );
};

export const useNestedLocationsContext = () => {
  const context = useContext(NestedLocationsContext);

  if (context === undefined) {
    throw new Error('useNestedLocationsContext must be used within an NestedLocationsProvider');
  }
  return context;
};
